// ** React Imports
import { useState, useEffect } from "react";

// ** MUI Imports
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  FormLabel,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { DateTimePicker } from '@mui/x-date-pickers-pro';
import { ToastContainer, toast } from "react-toastify";

// ** Icon Imports
import Icon from "components/icon";

// ** Custom Component Imports
import CustomTextField from "components/mui/text-field";
import dayjs from "dayjs";

// ** Logic
import { SaveTodo } from "../logic/ToDoLogic";

const AddToDo = ({ open, handleClose, fetchTodos }) => {
  const theme = useTheme();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [status, setStatus] = useState("InActive");
  const [errors, setErrors] = useState({ title: false, dueDate: false });

  useEffect(() => {
    if (open) {
      setTitle("");
      setDescription("");
      setDueDate(null);
      setStatus("InActive");
      setErrors({ title: false, dueDate: false });
    }
  }, [open]);

  const validate = () => {
    const newErrors = {
      title: !title.trim() || title.length < 2,
      dueDate: !dueDate,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const payload = {
        toDoListId: 0,
        name: title,
        description,
        dueDate: dueDate ? dayjs(dueDate).format("YYYY-MM-DDTHH:mm:ss") : null,
        status,
      };

      const result = await SaveTodo(payload, "save");

      if (result && !result.isError) {
        fetchTodos();
        handleClose();
      }
    } catch (e) {
      console.error(e);
      toast.error("An error occurred while saving the To-Do", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
    }
  };

  return (
    <>
      <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose}>
        <DialogTitle
          sx={{ borderBottom: "1px solid #D9D9D9", paddingBottom: "20px !important" }}
        >
          <Typography variant="h4" fontWeight="bold">
            Add To-Do
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={5}>
            <Grid item xs={12} md={7}>
              <CustomTextField
                fullWidth
                label={
                  <>
                    Title <span style={{ color: theme.palette.error.main }}>*</span>
                  </>
                }
                placeholder="Enter your desired title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setErrors((prev) => ({ ...prev, title: false }));
                }}
                error={errors.title}
                helperText={
                  errors.title ? "Title is required" : ""
                }
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <CustomTextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                placeholder="Enter a brief description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={7}>
              <FormLabel
                required
                sx={{
                  fontWeight: 400,
                  fontSize: "13px",
                  mb: "4px",
                  color: "text.primary",
                  "& .MuiFormLabel-asterisk": {
                    color: theme.palette.error.main,
                  },
                }}
              >
                Due Date
              </FormLabel>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  disablePast
                  value={dueDate}
                  onChange={(newVal) => {
                    setDueDate(newVal);
                    setErrors((prev) => ({ ...prev, dueDate: false }));
                  }}
                  sx={{
                    width: "100%",
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: errors.dueDate ? theme.palette.error.main : undefined
                      },
                      '&:hover fieldset': {
                        borderColor: errors.dueDate ? theme.palette.error.main : undefined
                      }
                    }
                  }}
                  slotProps={{
                    textField: {
                      size: "small",
                      error: errors.dueDate,
                      helperText: errors.dueDate ? "Due Date is required" : "",
                      FormHelperTextProps: { sx: { fontSize: "13px", ml: 0 } }
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            display: "flex",
            justifyContent: "space-between",
            padding: "20px 24px !important",
            boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
            zIndex: 1,
          }}
        >
          <Button variant="contained" color="info" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="main"
            onClick={handleSave}
            // disabled={!title.trim() || !dueDate}
          >
            Save To-Do
          </Button>
        </DialogActions>
      </Dialog>
      {/*<ToastContainer />*/}
    </>
  );
};

export default AddToDo;
